import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import config from '../../../../config';
import {DateUtility} from '../../../core/utils/date-utility';
import {endOfWeek, format, isSameDay, isSameMonth, startOfWeek} from 'date-fns';
import {
  CalendarDayViewBeforeRenderEvent,
  CalendarEvent,
  CalendarEventTitleFormatter,
  CalendarMonthViewDay,
  CalendarView,
  CalendarWeekViewBeforeRenderEvent,
} from 'angular-calendar';
import {DataBrokerService} from '../../../core/services/data-broker/data-broker.service';
import {LanguageService} from '../../../core/services/language/language.service';
import {ProviderService} from '../../../core/services/provider/provider.service';
import {UserSessionService} from '../../../core/services/user-session/user-session.service';
import {CustomEventTitleFormatter} from './custom-event-title-formatter.component';
import {Appointment} from '../../../core/models/appointment.model';
import {NavigationOption} from '../../../core/models/navigation-option';
import {MatDialog} from '@angular/material/dialog';
import {
  CalendarEventDialogComponent
} from '../../modals/calendar/calendar-event-dialog/calendar-event-dialog.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrgOverviewService} from 'src/app/modules/organizations/services/org-overview.service';
import {ExtendedOrganizationModel} from 'src/app/core/models/organization.model';
import {FormControl} from '@angular/forms';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {Utility} from 'src/app/core/utils/utility';
import {map, mergeMap, startWith, tap} from 'rxjs/operators';
import {Observable,} from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  host: {class: 'd-flex h-100'},
  styleUrls: ['./calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter,
    },
  ],
})
export class CalendarComponent implements OnInit {
  @Input() calendarEvents: CalendarEvent[] = [];
  @Input() selectedDate: Date;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() events = true;
  @Input() viewType: string;
  @Input() organization: ExtendedOrganizationModel;
  @Input() selectedClinic: ExtendedOrganizationModel;

  @Output() dayClicked = new EventEmitter<any>();
  @Output() dayEventClicked = new EventEmitter();
  @Output() calendarEvent = new EventEmitter();
  @Output() monthViewEvent = new EventEmitter<any>();
  @Output() monthNavigate = new EventEmitter<any>();
  @Output() clinicChange = new EventEmitter<ExtendedOrganizationModel>();

  @ViewChild('modalContent', {read: true}) modalContent: TemplateRef<any>;

  private navOptions: Array<NavigationOption>;

  private dashboardLabel: string;
  private appointmentLabel: string;

  public view: any = CalendarView.Month;
  CalendarView = CalendarView;

  public viewDate: Date = this.dateUtil.getToday().toDate();

  public clinicList: ExtendedOrganizationModel[] = [];
  // public filteredClinicList : ExtendedOrganizationModel[];
  clinic: ExtendedOrganizationModel;
  searchControl: FormControl = new FormControl();
  filteredClinicsList$: Observable<ExtendedOrganizationModel[]>;

  /*String Variables For Translation*/
  public eventActionLabel: string;
  public actionLabel: string;
  public eventLabel: string;
  public okLabel: string;
  public monthLabel: string;
  public weekLabel: string;
  public dayLabel: string;
  private calendarDateLabel: string;
  hourlyCovidVaccinationCount: HourlyCovidVaccinationCount[] = [];
  activeDayIsOpen = false;
  isLoaded = false;
  performerId: string;
  isClinicView: boolean;
  isAdmin: boolean = false;
  private privilege: any;

  @ContentChild('customEventTmpl') eventTmpl: TemplateRef<any>;
  startDate: Date;
  endDate: Date;

  private snackBarConf: MatSnackBarConfig = {
    duration: 0,
    horizontalPosition: 'center',
    verticalPosition: 'top',
  };

  constructor(
    private dateUtil: DateUtility,
    private data: DataBrokerService,
    private languageService: LanguageService,
    private session: UserSessionService,
    private providerService: ProviderService,
    public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private organizationService: OrgOverviewService,
    private snackBar: MatSnackBar,
  ) {
    this.updateLanguage();
    this.data
      .subscribe<any>('language-update')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateLanguage();
      });

    this.data.subscribe('organization-updated').pipe(
      mergeMap((res) => this.getOrganizationClinic(res as ExtendedOrganizationModel)),
      untilDestroyed(this)
    ).subscribe();
  }

  ngOnInit() {
    this.filteredClinicsList$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      // map(value => this._filter(value || '')),
      map((value) => (typeof value === 'string' ? value : value.name)),
      map((name) => (name ? this._filter(name) : this.clinicList.slice()))
    );

    this.isClinicView = this.viewType === 'clinic';

    if (!this.isClinicView) {
      this.setNavOptions();
    }

    if (this.minDate && !this.selectedDate) {
      this.viewDate = this.minDate;
    } else if (this.selectedDate) {
      this.viewDate = this.selectedDate;
    }

    this.performerId = this.session.sessionAccount.performerId;
    if (!this.calendarEvents) {
      this.getAppointmentsCount();
    } else {
      this.isLoaded = true;
    }
    this.getOrganizationClinic().subscribe();
    this.setAccessToDynamicClinic();
  }

  setAccessToDynamicClinic() {
    this.privilege = this.session.sessionAccount.privilege;
    this.isAdmin = +this.privilege.id === config.PRIVILEGEID.SUPER_ADMIN;
    if (!this.isAdmin)
      this.isAdmin = +this.privilege.id === config.PRIVILEGEID.ORGANIZATION_ADMIN;
    let account = this.session.sessionAccount;
    if (account.organizationId === this.organization.id)
      this.isAdmin = false;
  }

  ngOnDestroy(): void {
    this.data.publish<NavigationOption[]>([], 'nav-options');
  }

  private getAppointmentsCount() {
    this.isLoaded = false;
    const isSuperAdmin = +this.session.sessionAccount.privilege.id === config.PRIVILEGEID.SUPER_ADMIN;
    this.providerService
      .getAppointmentsForMonth(this.performerId, this.viewDate, isSuperAdmin)
      .subscribe((appointments: Appointment[]) => {
        this.calendarEvents = appointments.map(({dueDate, name: title, ...meta}) => ({
          start: new Date(dueDate),
          title,
          meta: {type: title, ...meta},
        }));

        this.calendarEvents = this.calendarEvents.sort((a, b) => {
          if (a.start.getTime() < b.start.getTime()) {
            return -1;
          }
          if (a.start.getTime() > b.start.getTime()) {
            return 1;
          }
          // a must be equal to b
          return 0;
        });
        this.isLoaded = true;
        this.cd.detectChanges();
      });
  }

  handleClickDay(viewDay: CalendarMonthViewDay): void {
    if (this.minDate && this.maxDate) {
      if (this.dateIsValid(viewDay.date)) {
        this.clickDay(viewDay);
      }
    } else {
      this.clickDay(viewDay);
    }
  }

  handleClickEvent(event: CalendarEvent) {
    this.dialog.open(CalendarEventDialogComponent, {
      position: {top: '0'},
      data: {event, performerId: this.performerId},
      disableClose: true
    });
  }

  setView(view: any) {
    if (typeof this.clinic === 'object' && this.searchControl.value !== '') {
      this.view = view;
      if (view == CalendarView.Week || view == CalendarView.Day) {
        this.getHourlyCovidVaccinationCount();
      } else if (view == CalendarView.Month) {
        this.monthNavigate.emit(this.viewDate);
        this.clinicChange.emit(this.clinic);
      }
    } else {
      this.snackBar.open('Please Select a Clinic', '', {...this.snackBarConf, duration: 2000});
    }

  }

  getHourlyCovidVaccinationCount() {
    let startDate = this.formatDate(this.viewDate);
    let endDate = startDate;
    if (this.view == CalendarView.Week) {
      startDate = this.formatDate(startOfWeek(this.viewDate));
      endDate = this.formatDate(endOfWeek(this.viewDate));
    }

    this.organizationService.getHourlyCovidVaccinationCount(this.clinic.id, startDate, endDate).subscribe((response: any[]) => {
      this.hourlyCovidVaccinationCount = response.map(({start, end, /*meta*/}) => ({
        start: Utility.utcToZonedDate(start, this.clinic.timeZoneId),
        end: Utility.utcToZonedDate(end, this.clinic.timeZoneId),
        title: '',
      }));
      this.cd.detectChanges();
    });
  }

  getOrganizationClinic(clinic?: ExtendedOrganizationModel) {
    if (clinic) this.selectedClinic = clinic;
    return this.organizationService.getOrgClinics(this.organization.id)
      .pipe(tap((res) => {
        this.clinicList = res;

        if (this.selectedClinic) {
          this.searchControl.setValue(this.selectedClinic.name);
          this.searchControl.markAsDirty();
          this.clinic = this.clinicList.find(c => c.id === this.selectedClinic.id);
        } else {
          this.clinic = this.clinicList.find(c => c.id === this.organization.id);
        }
      }))
  }

  formatDate(date): string {
    return format(date, 'yyyy-MM-dd');
  }

  clickDay(viewDay: CalendarMonthViewDay): void {
    const {date, events}: { date: Date; events: CalendarEvent[] } = viewDay;

    if (isSameDay(viewDay.date, this.viewDate) && this.activeDayIsOpen) {
      this.activeDayIsOpen = false;
    } else {
      if (isSameMonth(date, this.viewDate)) {
        this.viewDate = date;
        this.activeDayIsOpen = !!events.length;
      }
    }

    this.calendarEvent.emit(date);
  }

  dateIsValid(date: Date): boolean {
    return date >= this.minDate && date <= this.maxDate;
  }

  beforeMonthViewRender({body}: { body: CalendarMonthViewDay[] }): void {
    body.forEach((cell) => {
      const groups: any = {};
      cell.events.forEach((event: CalendarEvent<{ type: string }>) => {
        groups[event.meta.type] = groups[event.meta.type] || [];
        groups[event.meta.type].push(event);
      });
      cell['eventGroups'] = Object.entries(groups);
      if (!this.dateIsValid(cell.date)) {
        cell.cssClass = 'cal-disabled';
      } else {
        cell.cssClass = 'cal-bordered';
      }
    });
  }

  beforeWeekViewRender(event: CalendarWeekViewBeforeRenderEvent): void {
    this.startDate = event.period.start;
    this.endDate = event.period.end;
  }

  beforeDayViewRender(event: CalendarDayViewBeforeRenderEvent): void {
    this.startDate = event.period.start;
    this.endDate = event.period.end;
  }

  public viewDateChange(date) {
    this.activeDayIsOpen = false;
    this.viewDate = date;
    if (this.view == CalendarView.Week || this.view == CalendarView.Day) {
      this.getHourlyCovidVaccinationCount();
    } else if (this.view == CalendarView.Month)
      this.monthNavigate.emit(this.viewDate);
    this.changeClinic();
  }

  public changeClinic() {
    this.activeDayIsOpen = false;
    if (this.view == CalendarView.Week || this.view == CalendarView.Day) {
      this.getHourlyCovidVaccinationCount();
    } else
      this.clinicChange.emit(this.clinic);
  }

  private _filter(name: string) {
    const filterValue = name.toLowerCase();
    return this.clinicList.filter(p => p.name.toLowerCase().includes(filterValue));
  }

  resetSearchClinic() {
    if (!this.searchControl.dirty) {
      this.searchControl.setValue('');
    }
  }

  displayFn(clinic?: any): string | undefined {
    return clinic ? clinic.name : undefined
  }

  private setNavOptions() {
    this.navOptions = [
      {
        name: this.dashboardLabel,
        isSelected: true,
        parentRoute: '/curapatient/profile',
        route: '/curapatient/profile/dashboard',
      },
      {
        name: this.appointmentLabel,
        isSelected: false,
        parentRoute: '/curapatient/profile',
        route: '/curapatient/profile/calendar',
      },
    ];
    this.data.publish<Array<NavigationOption>>(this.navOptions, 'nav-options');
  }

  private updateLanguage() {
    this.eventActionLabel = this.languageService.language.calendar_eventActionLabel;
    this.actionLabel = this.languageService.language.calendar_actionLabel;
    this.eventLabel = this.languageService.language.calendar_eventLabel;
    this.okLabel = this.languageService.language.calendar_okLabel;
    this.monthLabel = this.languageService.language.calendar_monthLabel;
    this.weekLabel = this.languageService.language.calendar_weekLabel;
    this.dayLabel = this.languageService.language.calendar_dayLabel;
    this.calendarDateLabel = this.languageService.language.calendar_calendarDateLabel;
    this.dashboardLabel = this.languageService.language.profile_dashboardLabel;
    this.appointmentLabel = this.languageService.language.profile_appointmentLabel;
  }
}

export interface HourlyCovidVaccinationCount extends CalendarEvent {
  start: any;
  end: any;
  meta?: {
    scheduled: number;
    completed: number;
    capacity: number;
  }
}
